/**
 * External dependencies
 */
import React, { memo, useMemo, Fragment } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import ReportIcon from '@mui/icons-material/Report';

/**
 * Internal dependencies
 */
import { MessageSnackbarProps, MessageSnackbarItemProps } from './types';

/* Snack Item */
function SnackItem(props: MessageSnackbarItemProps): React.ReactElement {
  /* Global & Local States */
  const { snack } = props;

  /* Views */
  const renderIcon = useMemo(() => {
    switch (snack.typeMessage) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <CancelIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <ReportIcon color="info" />;
      default:
        return <Fragment />;
    }
  }, [snack]);

  /* Main */
  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{renderIcon}</Box>
      <Box>
        <Typography variant="body1" color="textPrimary" style={{ whiteSpace: 'pre-wrap' }}>
          {snack.typeContent || '-'}
        </Typography>
      </Box>
    </Box>
  );
}

function MessageSnackbar(props: MessageSnackbarProps): React.ReactElement {
  /* Global & Local States */
  const { list, removeSnack } = props;

  /* Views */
  const renderMessages = useMemo(() => {
    if (list.length > 0) {
      return <SnackItem snack={list[0]} removeSnack={removeSnack} />;
    }
    return <React.Fragment />;
  }, [list]);
  const renderIsMessageOpen = useMemo(() => {
    if (list.length > 0) {
      return true;
    }
    return false;
  }, [list]);

  /* Main */
  return (
    <Snackbar
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={renderIsMessageOpen}
      onClose={removeSnack}
      message={renderMessages}
      key=""
    />
  );
}

export default memo(MessageSnackbar);
